import React, { useState, useEffect, useCallback } from "react";
import safeToast from "utils/safeToast";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Badge,
  Chip,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ChatIcon from "@mui/icons-material/Chat";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Components
import PrivateChat from "./PrivateChat";

// Services
import friendService from "API/friendService";
import privateChatService from "API/privateChatService";
import { signalRManager } from "services/signalRManager";
import { useSignalR } from "hooks/useSignalR";
import { getFullUrl } from "config/api";
import { decryptText } from "config/encryption";

const WhatsAppChat = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { connection, isConnected, onlineUsers } = useSignalR();

  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [friends, setFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Get current user ID
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (userDetails) {
      try {
        const decrypted = decryptText(userDetails);
        const { StudentId } = JSON.parse(decrypted);
        setCurrentStudentId(StudentId);
      } catch (error) {
        console.error("Error parsing user details:", error);
      }
    }
  }, []);

  // Load friends
  const loadFriends = useCallback(async () => {
    if (!currentStudentId) return;
    
    setLoading(true);
    try {
      const data = await friendService.getFriends();
      setFriends(data || []);
      setFilteredFriends(data || []);
      
      // Load unread counts
      if (data && data.length > 0) {
        const counts = {};
        for (const friend of data) {
          const requesterId = friend.RequesterId || friend.requesterId;
          const receiverId = friend.ReceiverId || friend.receiverId;
          const friendId = requesterId === currentStudentId ? receiverId : requesterId;
          
          if (friendId && friendId !== currentStudentId) {
            try {
              const countResponse = await privateChatService.getUnreadCount(friendId);
              const count = countResponse?.count || countResponse || 0;
              counts[friendId] = count;
            } catch (error) {
              console.error(`Error loading unread count for friend ${friendId}:`, error);
              counts[friendId] = 0;
            }
          }
        }
        setUnreadCounts(counts);
      }
    } catch (error) {
      try {
        safeToast.error("Failed to load friends");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error loading friends:", error);
    } finally {
      setLoading(false);
    }
  }, [currentStudentId]);

  useEffect(() => {
    if (currentStudentId) {
      loadFriends();
    }
  }, [currentStudentId, loadFriends]);

  // Filter friends based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFriends(friends);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = friends.filter((friend) => {
      const requesterId = friend.RequesterId || friend.requesterId;
      const receiverId = friend.ReceiverId || friend.receiverId;
      const friendId = requesterId === currentStudentId ? receiverId : requesterId;
      
      const friendName = requesterId === currentStudentId
        ? (friend.ReceiverName || friend.receiverName || "")
        : (friend.RequesterName || friend.requesterName || "");
      const friendUserName = requesterId === currentStudentId
        ? (friend.ReceiverUserName || friend.receiverUserName || "")
        : (friend.RequesterUserName || friend.requesterUserName || "");
      
      return (
        friendName.toLowerCase().includes(query) ||
        friendUserName.toLowerCase().includes(query)
      );
    });
    setFilteredFriends(filtered);
  }, [searchQuery, friends, currentStudentId]);

  // Update unread count for a specific friend
  const updateUnreadCount = useCallback(async (friendId) => {
    if (!friendId || friendId === currentStudentId) return;
    
    try {
      const countResponse = await privateChatService.getUnreadCount(friendId);
      const count = countResponse?.count || countResponse || 0;
      setUnreadCounts((prev) => ({ ...prev, [friendId]: count }));
    } catch (error) {
      console.error(`Error updating unread count for friend ${friendId}:`, error);
    }
  }, [currentStudentId]);

  // SignalR event handlers
  useEffect(() => {
    if (!connection || !isConnected || !currentStudentId) return;

    const handleReceivePrivateMessage = async (privateMessage) => {
      if (privateMessage && privateMessage.toStudentId === currentStudentId) {
        const senderId = privateMessage.fromStudentId;
        if (senderId) {
          await updateUnreadCount(senderId);
        }
      }
    };

    const handlePrivateMessageRead = async (readMessage) => {
      if (readMessage && readMessage.fromStudentId === currentStudentId) {
        const recipientId = readMessage.toStudentId;
        if (recipientId) {
          await updateUnreadCount(recipientId);
        }
      }
    };

    connection.on("ReceivePrivateMessage", handleReceivePrivateMessage);
    connection.on("PrivateMessageRead", handlePrivateMessageRead);

    return () => {
      connection.off("ReceivePrivateMessage", handleReceivePrivateMessage);
      connection.off("PrivateMessageRead", handlePrivateMessageRead);
    };
  }, [connection, isConnected, currentStudentId, updateUnreadCount]);

  // Check if user is online
  const isUserOnline = useCallback((studentId) => {
    if (!onlineUsers || onlineUsers.length === 0) return false;
    return onlineUsers.some(user => {
      const userId = user.studentId || user.StudentId;
      const userIdNum = typeof userId === 'string' ? parseInt(userId) : userId;
      const targetId = typeof studentId === 'string' ? parseInt(studentId) : studentId;
      return userIdNum === targetId;
    });
  }, [onlineUsers]);

  // Handle friend selection
  const handleFriendSelect = (friend) => {
    const requesterId = friend.RequesterId || friend.requesterId;
    const receiverId = friend.ReceiverId || friend.receiverId;
    const friendId = requesterId === currentStudentId ? receiverId : requesterId;
    
    const friendName = requesterId === currentStudentId
      ? (friend.ReceiverName || friend.receiverName || "Unknown")
      : (friend.RequesterName || friend.requesterName || "Unknown");
    
    const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
    const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
    const profileImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
    
    const friendUserName = requesterId === currentStudentId
      ? (friend.ReceiverUserName || friend.receiverUserName)
      : (friend.RequesterUserName || friend.requesterUserName);
    
    const imageUrl = profileImageUrl
      ? profileImageUrl.startsWith('http')
        ? profileImageUrl
        : getFullUrl(profileImageUrl)
      : null;

    setSelectedFriend({
      friendId,
      friendName,
      profileImageUrl: imageUrl,
      friendUserName,
    });
    
    // Clear unread count when chat opens
    if (friendId) {
      setUnreadCounts((prev) => ({ ...prev, [friendId]: 0 }));
      setTimeout(() => {
        updateUnreadCount(friendId);
      }, 1000);
    }
  };

  // Get last message preview (optional - can be enhanced later)
  const getLastMessagePreview = (friend) => {
    // This can be enhanced to show last message from chat history
    return "";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          height: "calc(100vh - 120px)",
          display: "flex",
          bgcolor: theme.palette.background.default,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Left Sidebar - Friends List */}
        <Box
          sx={{
            width: isMobile && selectedFriend ? 0 : { xs: "100%", md: "400px" },
            display: { xs: isMobile && selectedFriend ? "none" : "flex", md: "flex" },
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            borderRight: "1px solid",
            borderColor: "divider",
            transition: "width 0.3s ease",
            overflow: "hidden",
          }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 0,
            borderBottom: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.9375rem" }}>
            Chat
          </Typography>
        </Paper>

        {/* Search Bar */}
        <Box sx={{ p: 1.5, bgcolor: theme.palette.background.default }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
        </Box>

        {/* Friends List */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : filteredFriends.length === 0 ? (
            <Box textAlign="center" py={4}>
              <ChatIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography color="text.secondary">No friends found</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredFriends.map((friend, index) => {
                const requesterId = friend.RequesterId || friend.requesterId;
                const receiverId = friend.ReceiverId || friend.receiverId;
                const friendId = requesterId === currentStudentId ? receiverId : requesterId;
                
                const friendName = requesterId === currentStudentId
                  ? (friend.ReceiverName || friend.receiverName || "Unknown")
                  : (friend.RequesterName || friend.requesterName || "Unknown");
                
                const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
                const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
                const profileImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
                
                const imageUrl = profileImageUrl
                  ? profileImageUrl.startsWith('http')
                    ? profileImageUrl
                    : getFullUrl(profileImageUrl)
                  : null;
                
                const isSelected = selectedFriend?.friendId === friendId;
                const isOnline = isUserOnline(friendId);
                const unreadCount = unreadCounts[friendId] || 0;

                return (
                  <React.Fragment key={friend.RequestId || index}>
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: isSelected ? alpha(theme.palette.success.main, 0.1) : "transparent",
                        "&:hover": {
                          bgcolor: isSelected
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.action.hover, 0.5),
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleFriendSelect(friend)}
                        sx={{
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <ListItemAvatar>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            badgeContent={
                              isOnline ? (
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: 12,
                                    color: "success.main",
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : null
                            }
                          >
                            <Avatar
                              src={imageUrl}
                              sx={{
                                width: 50,
                                height: 50,
                              }}
                            >
                              {friendName.charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography
                                variant="body2"
                                fontWeight={unreadCount > 0 ? 600 : 400}
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: "text.primary",
                                }}
                              >
                                {friendName}
                              </Typography>
                              {unreadCount > 0 && (
                                <Badge
                                  badgeContent={unreadCount > 99 ? "99+" : unreadCount}
                                  color="error"
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      fontSize: "0.7rem",
                                      height: 20,
                                      minWidth: 20,
                                      fontWeight: 600,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                mt: 0.5,
                              }}
                            >
                              {isOnline ? "Online" : "Offline"}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredFriends.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
        </Box>

        {/* Right Side - Chat Interface */}
        <Box
          sx={{
            flex: 1,
            display: { xs: selectedFriend ? "flex" : "none", md: "flex" },
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            position: "relative",
          }}
        >
        {selectedFriend ? (
          <PrivateChat
            otherStudentId={selectedFriend.friendId}
            otherStudentName={selectedFriend.friendName}
            otherProfileImageUrl={selectedFriend.profileImageUrl}
            onClose={isMobile ? () => setSelectedFriend(null) : null}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <ChatIcon sx={{ fontSize: 120, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={300}>
              Select a friend to start chatting
            </Typography>
          </Box>
        )}
        </Box>
      </Box>
      <Footer />
    </DashboardLayout>
  );
};

export default WhatsAppChat;

